import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { LOCALES, type Locale } from '@rental/domain'
import { format, formatTime, messagesFor } from '@rental/i18n'
import { Button, Notice, Spinner } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useCallback, useEffect, useId, useState } from 'react'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { PageHeader } from '../components/PageHeader'
import { ReasonList } from '../components/SubPanel'
import { TermsApproval } from '../components/TermsText'
import { PageHeading } from '../components/ui'
import { useLocale } from '../i18n'
import { onResume } from '../resume'
import { ContractView } from '../signing/ContractView'
import { type Drawn, SignaturePad } from '../signing/SignaturePad'

type View = ApiSchemas['SigningView']

/** A problem in the customer's language: the booking site's words for the codes it shares. */
function customerProblem(problem: ApiProblem, locale: Locale): string {
  const m = messagesFor(locale)
  if (problem.kind === 'network') return m.errors.network
  const codes: Readonly<Record<string, string>> = m.errors.codes
  return codes[problem.code] ?? codes.internal_error ?? m.errors.network
}

/**
 * UI-1d: the customer's screen stands on panes over the depth, as the lock
 * screen does, every word on a pane, in the customer's rules (DESIGN.md): the
 * customer's action is the gold button. The words are in the booking's
 * language, once it is known.
 */
function Stage({ locale, children }: { locale?: Locale; children: ReactNode }) {
  return (
    <>
      <div className="depth" aria-hidden="true" />
      <main
        id="main"
        {...(locale ? { lang: locale } : {})}
        className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col gap-6 p-4"
      >
        {children}
      </main>
    </>
  )
}

/**
 * CTR-2: the iPad turned to the customer. The ARCHITECTURE's locked route: it
 * shows the contract and the two signature boxes and nothing else; there is
 * no menu and no way back but handing the iPad to staff, whose PIN follows.
 * The contract is Italian; the words around it are in the booking's language.
 */
export function SigningScreen() {
  const view = useQuery({
    queryKey: ['signing'],
    queryFn: () => load(call(api.GET('/v1/staff/signing'))),
    staleTime: Number.POSITIVE_INFINITY,
    // Tried with or without a connection: by default React Query waits for one
    // before trying, or trying again, and the screen would spin until it came
    // (C7). That default would also stop it trying again once the connection
    // is back (`refetchOnReconnect` follows `networkMode`), so it is asked for:
    // a query with no contract is always stale, whatever `staleTime` says.
    networkMode: 'always',
    refetchOnReconnect: true,
  })
  const { refetch } = view
  const retry = useCallback(() => void refetch(), [refetch])
  if (view.data) return <SigningForm view={view.data} />
  // A dead grant is the access gate's (back to the lock screen). Anything else
  // says so, instead of spinning until the grant runs out (C7). Counted, not the
  // error itself: trying again without a contract clears the error (React
  // Query's `fetchState`), and the screen would fall back to the spinner.
  if (view.errorUpdateCount > 0) return <Unavailable retrying={view.isFetching} retry={retry} />
  return (
    <Stage>
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="text-text-muted" />
      </div>
    </Stage>
  )
}

/**
 * C7: the contract could not be read. The booking's language comes with the
 * contract that did not, so the customer is asked to hand the iPad back in
 * the booking site's three languages; the staff's own words then try again
 * or take it back. A connection back tries again by itself (React Query's
 * reconnect). So does the app back in front, through `resume.ts`, whose
 * signal from the iPad app's shell React Query does not hear.
 */
function Unavailable({ retrying, retry }: { retrying: boolean; retry: () => void }) {
  const { endSigning } = useAccess()
  const { messages: m } = useLocale()
  useEffect(() => onResume(retry), [retry])
  return (
    <Stage>
      <div className="glass-pane-strong flex flex-col gap-4 rounded-lg p-6">
        <PageHeading className="text-3xl">{m.signingScreen.unavailable}</PageHeading>
        <Notice tone="danger" live="assertive">
          {LOCALES.map((locale) => (
            <p key={locale} lang={locale}>
              {messagesFor(locale).signing.unavailable}
            </p>
          ))}
        </Notice>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button variant="staff" busy={retrying} onClick={retry}>
            {m.app.retry}
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => void endSigning()}>
            {m.signingScreen.takeBack}
          </Button>
        </div>
      </div>
    </Stage>
  )
}

function SigningForm({ view }: { view: View }) {
  const { endSigning, takeOver } = useAccess()
  const words = messagesFor(view.locale).signing
  const [main, setMain] = useState<Drawn | null>(null)
  const [approval, setApproval] = useState<Drawn | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [signed, setSigned] = useState(false)
  const reasonsId = useId()

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!main || !approval || busy) return
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/signing/sign', {
        body: { sha256: view.sha256, main, specificApproval: approval },
      }),
    )
    setBusy(false)
    if (result.ok) {
      setSigned(true)
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  if (signed) {
    return (
      <Stage locale={view.locale}>
        <div className="glass-pane-strong flex flex-col gap-4 rounded-lg p-6">
          <PageHeading className="text-3xl">{words.signedTitle}</PageHeading>
          <Notice tone="success" live="polite">
            <p>{words.signedBody}</p>
            {view.document.customer.email ? <p>{words.signedEmail}</p> : null}
          </Notice>
          <div>
            <Button onClick={() => void endSigning()}>{words.done}</Button>
          </div>
        </div>
      </Stage>
    )
  }

  const ready = main !== null && approval !== null
  // A button that waits says why, beside it, and names it.
  const reasons = ready || busy ? [] : [words.submitHint]
  return (
    <Stage locale={view.locale}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-6">
        <PageHeader
          title={words.title}
          intro={words.intro}
          meta={format(words.expires, { time: formatTime(view.locale, view.expiresAt) })}
        >
          {view.document.terms.draft ? (
            <Notice tone="warning">
              <p>{words.draft}</p>
            </Notice>
          ) : null}
          {view.locale !== 'it' ? (
            <Notice tone="info">
              <p>{words.language}</p>
            </Notice>
          ) : null}
        </PageHeader>

        <div className="glass-pane rounded-card p-4 sm:p-6">
          <ContractView document={view.document} />
        </div>

        <section className="glass-pane flex flex-col gap-4 rounded-card p-4 sm:p-6">
          <SignaturePad
            label={words.signMain}
            hint={words.pad}
            emptyText={words.padEmpty}
            signedText={words.padSigned}
            clearText={words.clear}
            onChange={setMain}
          />
        </section>

        <section className="glass-pane flex flex-col gap-4 rounded-card p-4 sm:p-6">
          <TermsApproval text={view.document.terms.specificApproval} />
          <SignaturePad
            label={words.signApproval}
            hint={words.pad}
            emptyText={words.padEmpty}
            signedText={words.padSigned}
            clearText={words.clear}
            onChange={setApproval}
          />
        </section>

        <div className="glass-pane flex flex-col gap-3 rounded-card p-4 sm:p-6">
          {problem ? (
            <Notice tone="danger" live="assertive">
              <p>{customerProblem(problem, view.locale)}</p>
            </Notice>
          ) : null}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              type="submit"
              busy={busy}
              disabled={!ready && !busy}
              aria-describedby={reasons.length > 0 ? reasonsId : undefined}
            >
              {words.submit}
            </Button>
            <Button variant="link" onClick={() => void endSigning()}>
              {words.cancel}
            </Button>
          </div>
          <ReasonList id={reasonsId} reasons={reasons} />
        </div>
      </form>
    </Stage>
  )
}
